(() => {
  const selector = '[data-exact-store-stock]';
  const config = window.ThistleInStoreStockConfig || {};
  const apiVersion = config.apiVersion || '2026-07';
  const cache = new Map();
  let refreshTimer;

  const variantGid = (id) => {
    const value = String(id || '').trim();
    if (!value) return null;
    return value.startsWith('gid://') ? value : `gid://shopify/ProductVariant/${value}`;
  };

  const tailId = (gid) => String(gid || '').split('/').pop();

  const escapeHtml = (value) =>
    String(value ?? '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');

  const getTargets = (root = document) => {
    const targets = [];
    if (root?.matches?.(selector)) targets.push(root);
    root?.querySelectorAll?.(selector).forEach((node) => targets.push(node));
    return targets;
  };

  const renderUnavailable = (target, message = 'Exact store stock unavailable') => {
    const container = target.querySelector('[data-stock-locations]');
    if (!container) return;

    if (target.dataset.stockMode === 'card') {
      container.innerHTML = `<span class="is-unknown">${escapeHtml(message)}</span>`;
    } else {
      container.innerHTML = `
        <div class="instore-only-location instore-only-location--unknown">
          <span>${escapeHtml(message)}</span>
        </div>
      `;
    }

    target.dataset.stockState = 'error';
  };

  const renderStock = (target, stores) => {
    const container = target.querySelector('[data-stock-locations]');
    if (!container) return;

    const sortedStores = [...(stores || [])]
      .filter((store) => store?.location?.name)
      .sort((a, b) => a.location.name.localeCompare(b.location.name));

    if (!sortedStores.length) {
      renderUnavailable(target, 'Not stocked at our pickup locations');
      return;
    }

    if (target.dataset.stockMode === 'card') {
      container.innerHTML = sortedStores
        .map((store) => {
          const inStock = Number(store.quantityAvailable || 0) > 0;
          return `
            <span
              class="${inStock ? 'is-available' : 'is-unavailable'}"
              data-location-id="${escapeHtml(tailId(store.location.id))}"
              data-physical-quantity="${Number(store.quantityAvailable || 0)}"
            >
              ${inStock ? 'In stock' : 'Out of stock'} ${escapeHtml(store.location.name)}
            </span>
          `;
        })
        .join('');
    } else {
      container.innerHTML = sortedStores
        .map((store) => {
          const quantity = Number(store.quantityAvailable || 0);
          const inStock = quantity > 0;
          return `
            <div
              class="instore-only-location${inStock ? '' : ' instore-only-location--unavailable'}"
              data-location-id="${escapeHtml(tailId(store.location.id))}"
              data-physical-quantity="${quantity}"
            >
              <span class="instore-only-location__dot" aria-hidden="true"></span>
              <span class="instore-only-location__copy">
                <strong>${inStock ? 'In stock' : 'Out of stock'}</strong>
                <span class="instore-only-location__name">${escapeHtml(store.location.name)}</span>
              </span>
            </div>
          `;
        })
        .join('');
    }

    target.dataset.stockState = 'loaded';
  };

  const fetchVariants = async (variantIds) => {
    const uncached = variantIds.filter((id) => !cache.has(id));
    if (!uncached.length) return;

    if (!config.domain || !config.token) {
      uncached.forEach((id) => cache.set(id, { error: 'missing-token' }));
      return;
    }

    const query = `
      query ExactStoreStock($ids: [ID!]!) {
        nodes(ids: $ids) {
          ... on ProductVariant {
            id
            storeAvailability(first: 50) {
              nodes {
                quantityAvailable
                location {
                  id
                  name
                }
              }
            }
          }
        }
      }
    `;

    const response = await fetch(`https://${config.domain}/api/${apiVersion}/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': config.token,
      },
      body: JSON.stringify({
        query,
        variables: { ids: uncached.map(variantGid) },
      }),
    });

    if (!response.ok) {
      throw new Error(`Storefront API returned ${response.status}`);
    }

    const payload = await response.json();
    if (payload.errors?.length) {
      throw new Error(payload.errors.map((error) => error.message).join('; '));
    }

    const returned = new Set();
    (payload.data?.nodes || []).forEach((node) => {
      if (!node?.id) return;
      const id = tailId(node.id);
      returned.add(id);
      cache.set(id, {
        stores: node.storeAvailability?.nodes || [],
      });
    });

    uncached.forEach((id) => {
      if (!returned.has(String(id))) {
        cache.set(String(id), { error: 'not-found' });
      }
    });
  };

  const refresh = async (root = document) => {
    const targets = getTargets(root);
    if (!targets.length) return;

    const ids = [...new Set(
      targets
        .map((target) => String(target.dataset.variantId || '').trim())
        .filter(Boolean)
    )];

    if (!ids.length) return;

    targets.forEach((target) => {
      const container = target.querySelector('[data-stock-locations]');
      if (container && target.dataset.stockState !== 'loaded') {
        container.setAttribute('aria-busy', 'true');
      }
    });

    try {
      await fetchVariants(ids);
    } catch (error) {
      ids.forEach((id) => cache.set(String(id), { error: error.message || 'request-failed' }));
    }

    targets.forEach((target) => {
      const id = String(target.dataset.variantId || '').trim();
      const result = cache.get(id);
      const container = target.querySelector('[data-stock-locations]');
      container?.removeAttribute('aria-busy');

      if (!result || result.error) {
        const message =
          result?.error === 'missing-token'
            ? 'Exact store stock is not configured'
            : 'Unable to check store stock right now';
        renderUnavailable(target, message);
        return;
      }

      renderStock(target, result.stores);
    });
  };

  const scheduleRefresh = (root = document) => {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => refresh(root), 40);
  };

  window.ThistleInStoreStock = {
    refresh,
    clearVariant(variantId) {
      cache.delete(String(variantId || ''));
    },
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => refresh(document), { once: true });
  } else {
    refresh(document);
  }

  document.addEventListener('shopify:section:load', (event) => refresh(event.target));
  document.addEventListener('thistle:instore-stock:refresh', (event) => {
    const root = event.detail?.root || document;
    scheduleRefresh(root);
  });
})();
